import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
});

// Attach access token from storage as fallback (cookies work too).
// Prefer session (Remember-me OFF) over persistent so the newer session wins
// immediately after a fresh sign-in.
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('clanster_access_token')
    || localStorage.getItem('clanster_access_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default api;

/**
 * Resolve an avatar value (from user.avatar or creator.avatar) to a src URL
 * that can be dropped straight into an <img>. Returns null when there's no
 * usable value so callers can fall back to an initials placeholder.
 *
 * Supports: data URLs (legacy base64), absolute http(s) URLs, and object
 * storage paths served by GET /api/files/{path}.
 */
export function resolveAvatarUrl(avatar) {
  if (!avatar || typeof avatar !== 'string') return null;
  if (avatar.startsWith('data:')) return avatar;
  if (avatar.startsWith('http://') || avatar.startsWith('https://')) return avatar;
  // Storage path (e.g. "clanster/uploads/<uid>/<uuid>.jpg")
  return `${API_BASE}/api/files/${avatar.replace(/^\/+/, '')}`;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

/**
 * Upload an image to POST /api/files/upload and return BOTH the storage path
 * (relative, used for persistence like avatar/cover fields) AND a fully-
 * qualified URL ready to drop into <img src>.
 *
 * The backend returns `{path: 'clanster/uploads/<uid>/<uuid>.png'}` — this
 * helper wraps that contract so callers can't accidentally read `data.url`
 * (which doesn't exist and silently caused iter99's DM image bug).
 *
 * Example:
 *   const { path, url } = await uploadImage(file);
 *   setPreview(url);        // for immediate display
 *   setAvatar(path);        // for storage on the user document
 *
 * Throws with a friendly message if the upload failed or the response is
 * malformed. Client size / type validation is the caller's job.
 */
export async function uploadImage(file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/files/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const path = data?.path;
  if (!path) throw new Error('Upload returned no path.');
  const base = (API_BASE || '').replace(/\/$/, '');
  const url = `${base}/api/files/${path.replace(/^\/+/, '')}`;
  return { path, url };
}
